import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';
import { breakpoints, Button, fontSize, palette, spacing } from '@playdapp/ui';

import Color from 'styles/Color';
import { Tab } from 'types/merge';

const slideLeft = keyframes`
  0% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(0);
  }
`;

export const PageBlock = styled.div`
  ${breakpoints.down('md')} {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow-y: auto;
    z-index: 10;
    background-color: #ffffff;
    transition: left 0.5s;
    animation: ${slideLeft} 0.5s;
  }
`;

export const PageWrapper = styled.div`
  ${breakpoints.down('md')} {
    padding: 0 ${spacing.xl};
    margin-top: -42px;
  }
`;

export const MobileHeader = styled.div`
  display: none;

  ${breakpoints.down('md')} {
    position: sticky;
    top: 0;
    left: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    height: 4rem;
    padding: 0 ${spacing.xl};
    background-color: #ffffff;
    z-index: 3;
  }
`;

export const MobileButtonBlock = styled.div`
  display: none;

  ${breakpoints.down('md')} {
    position: sticky;
    top: calc(100% - 62px);
    display: flex;
    justify-content: center;
    align-items: center;
    width: ${({ theme }) => theme.rem(318)};
    height: 2.5rem;
    margin: 0 auto;
    z-index: 2;
  }
`;

export const MobileClearButton = styled(Button)`
  margin-right: ${spacing.m};
`;

export const MobileButton = styled(Button)`
  z-index: 2;
`;

export const MobileHeaderTitle = styled.div`
  span {
    font-weight: 600;
    color: ${palette.primary600};
  }
`;

export const MergeBlock = styled.div`
  width: 100%;

  ${breakpoints.down('md')} {
    margin-top: ${spacing.xs};
  }
`;

export const TabBlock = styled.div`
  display: flex;
  width: 100%;
  height: 3rem;
`;

export const TabItem = styled.div<{ isSelected: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  max-width: 274px;
  height: 100%;
  border: 1px solid ${palette.gray400};
  border-radius: 8px 8px 0 0;
  border-bottom: none;
  z-index: 2;
  background-color: ${palette.gray400};
  cursor: pointer;
  font-weight: 600;
  color: ${palette.gray900};

  :nth-of-type(2) {
    margin-left: ${spacing.xxs};
    margin-right: ${spacing.xxs};
  }

  ${({ isSelected }) =>
    isSelected &&
    css`
      background-color: #ffffff;
      color: ${palette.black};

      ::after {
        content: '';
        position: absolute;
        bottom: -1px;
        width: 100%;
        border-bottom: 1px solid white;
      }
    `};

  ${breakpoints.down('md')} {
    font-size: ${fontSize.p4};
  }

  @media (max-width: 480px) {
    :nth-of-type(2) {
      margin-left: 2px;
      margin-right: 2px;
    }
  }
`;

export const TabNotification = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background-color: ${palette.red500};
`;

export const DragonTextBox = styled.div`
  text-align: center;
  line-height: 16px;

  .light {
    font-weight: 400;
  }
`;

export const CharacterBlock = styled.div<{
  isDragon?: boolean;
}>`
  display: grid;
  grid-template-columns: 2fr 1fr;
  padding: ${spacing.xl};
  padding-right: 0 !important;
  background-color: #ffffff;
  border: 1px solid ${palette.gray400};
  border-radius: 0 16px 16px 16px;
  z-index: 1;
  overflow: hidden;

  ${breakpoints.down('xxl')} {
    ${({ isDragon }) =>
      isDragon &&
      css`
        grid-template-columns: repeat(2, 1fr);
      `}
  }

  ${breakpoints.down('lg')} {
    padding: ${spacing.m};
  }

  @media (max-width: 900px) {
    border-radius: 0 0 16px 16px;
  }

  ${breakpoints.down('md')} {
    display: block;
    padding: 0;
  }
`;

export const CharacterSection = styled.div<{
  isDragon?: boolean;
}>`
  display: grid;
  grid-template-columns: ${({ isDragon }) => !isDragon && 'repeat(2, 1fr)'};
  grid-gap: ${spacing.m};
  z-index: 2;

  ${breakpoints.down('xxl')} {
    ${({ isDragon }) =>
      isDragon &&
      css`
        display: block;
      `}
  }

  ${breakpoints.down('lg')} {
    display: block;
  }
`;

export const CharacterBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${spacing.xl};
  background-color: ${palette.gray100};
  border: 1px solid ${palette.gray300};
  border-radius: 12px;

  ${breakpoints.down('lg')} {
    margin-top: ${spacing.m};
  }

  ${breakpoints.down('md')} {
    padding: 0 0 ${spacing.m} 0;
    background-color: #ffffff;
    border: none;
    border-bottom: 1px solid ${palette.gray300};
  }

  @media (max-width: 359px) {
    padding: ${spacing.s} 0;
  }
`;

export const CharacterTitleBlock = styled.div`
  display: flex;
  margin-bottom: ${spacing.l};

  span {
    font-size: ${fontSize.p3};
    font-weight: 600;
    color: ${palette.black};
  }

  span.joker {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 15px;
    margin-top: ${spacing.xxs};
    margin-left: ${spacing.xs};
    font-size: ${fontSize.p4};
    border-radius: 2px;
    color: #ffffff;
    background: linear-gradient(136.35deg, #9b6cea 1.37%, #06acf9 97.61%);
  }
`;

export const CharacterListBlock = styled.div<{
  isSrPlaydappTown?: boolean;
  isSingle?: boolean;
  isDragon?: boolean;
}>`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: ${spacing.l};

  ${({ isSingle }) =>
    isSingle &&
    css`
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
    `};

  ${breakpoints.up('xxl')} {
    ${({ isDragon }) =>
      isDragon &&
      css`
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
      `};
  }

  ${breakpoints.down('xxl')} {
    grid-template-columns: ${({ isSrPlaydappTown }) =>
      isSrPlaydappTown ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)'};

    ${({ isDragon }) =>
      isDragon &&
      css`
        display: grid;
        grid-template-columns: repeat(3, 1fr);
      `}
  }

  ${breakpoints.down('xl')} {
    grid-template-columns: ${({ isDragon }) =>
      isDragon ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)'};
  }

  ${breakpoints.down('lg')} {
    grid-template-columns: repeat(3, 1fr);
    grid-gap: ${spacing.s};
  }
`;

export const Character = styled.div<{
  isPurchase?: boolean;
  isSelected?: boolean;
  isCursorPointer?: boolean;
}>`
  position: relative;
  border: 3px solid transparent;
  border-radius: 8px;
  background-image: linear-gradient(#fff, #fff),
    linear-gradient(to right, #74d1fc 0%, #2bcab0 100%);
  background-origin: border-box;
  background-clip: content-box, border-box;
  cursor: ${({ isCursorPointer }) => (isCursorPointer ? 'pointer' : 'auto')};

  ${({ isPurchase }) =>
    isPurchase &&
    css`
      filter: grayscale(100%);
    `};

  div.selected {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    top: 0;
    width: 100%;
    height: 100%;
    border-radius: 4px;
    background: rgba(71, 215, 191, 0.4);
    backdrop-filter: blur(4px);
  }
`;

export const CharacterCount = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  right: -12px;
  top: -12px;
  width: 1.5rem;
  height: 1.5rem;
  font-size: ${fontSize.p4};
  font-weight: 600;
  color: #ffffff;
  background-color: ${palette.primary600};
  border-radius: 50%;
  z-index: 1;
`;

export const CharacterButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 6.875rem;
  height: 1.5rem;
  margin-top: ${spacing.s};
  font-size: ${fontSize.p4};
  font-weight: 600;

  ${breakpoints.down('sm')} {
    max-width: 84px;
  }

  @media (max-width: 359px) {
    max-width: 64px;
    font-size: ${fontSize.p5};
  }
`;

export const JokerCountBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 110px;
  height: 1.5rem;
  margin-top: ${spacing.s};

  ${breakpoints.down('sm')} {
    width: 84px;
  }

  @media (max-width: 359px) {
    width: 64px;
  }
`;

export const JokerCountButton = styled(Button)`
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border-radius: 50%;

  ${breakpoints.down('md')} {
    font-size: ${fontSize.p4};
  }

  @media (max-width: 359px) {
    width: 1.25rem;
    height: 1.25rem;
    padding: 0;
    font-size: ${fontSize.p5};
  }
`;

export const MergeButtonSection = styled.div<{ isDragon: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: ${({ isDragon }) => (isDragon ? 'row' : 'column')};
  width: 100%;
  z-index: 1;

  .merge-button-bg01,
  .merge-button-bg02 {
    position: absolute;
    width: 100%;
    height: 100%;
    background-repeat: no-repeat;
  }

  .merge-button-bg01 {
    top: -5%;
    left: -30%;
    background-image: -webkit-image-set(
      url(https://images.playdapp.com/marketplace-dev/merge/merge-button-bg01.png) 1x,
      url(https://images.playdapp.com/marketplace-dev/merge/merge-button-bg01@2x.png) 2x
    );
    background-size: 100% 100%;
  }

  .merge-button-bg02 {
    top: 0;
    left: 10%;
    background-image: -webkit-image-set(
      url(https://images.playdapp.com/marketplace-dev/merge/merge-button-bg02_2.png) 1x,
      url(https://images.playdapp.com/marketplace-dev/merge/merge-button-bg02_2@2x.png) 2x
    );
    background-size: 100% 100%;
  }

  span {
    width: 100% !important;
    max-width: 155.45px;
  }

  ${breakpoints.down('xxl')} {
    .merge-button-bg01 {
      left: -40%;
      background-size: 100% 75%;
    }
  }

  ${breakpoints.down('lg')} {
    flex-direction: column;

    .merge-button-bg01 {
      top: 5%;
      left: -40%;
      background-size: 110% 60%;
    }
  }

  ${breakpoints.down('md')} {
    display: none;
  }
`;

export const MergeButtonImageBox = styled.div<{ selectedTab: Tab }>`
  width: 183px;
  height: 209px;
  background-image: ${({ selectedTab }) =>
    selectedTab &&
    `-webkit-image-set(
    url(https://images.playdapp.com/marketplace-dev/merge/${selectedTab.toLocaleLowerCase()}_result2.png) 1x,
    url(https://images.playdapp.com/marketplace-dev/merge/${selectedTab.toLocaleLowerCase()}_result2@2x.png) 2x
  )`};
  z-index: 1;

  ${({ selectedTab }) =>
    selectedTab === 'PLAYDAPP_DRAGON_R' &&
    css`
      width: 156px;
      height: 180px;
      margin-right: ${spacing.xxl};

      ${breakpoints.down('lg')} {
        margin-right: 0;
      }
    `}
`;

export const MergeButtonBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

export const SelectedStatusBox = styled.div`
  z-index: 0;

  span {
    color: ${palette.black};

    &.strong {
      font-size: ${fontSize.h3};
      font-weight: 600;
      color: ${palette.primary600};
    }

    &.max {
      font-size: ${fontSize.p1};
      font-weight: 600;
    }
  }
`;

export const SelectedJokerStatusBox = styled.div`
  z-index: 0;
  text-align: center;

  span.strong {
    font-size: ${fontSize.p1};
    font-weight: 600;
    color: ${palette.primary600};
  }
`;

export const CustomButton = styled(Button)`
  margin-top: ${spacing.m};
  z-index: 0;
`;

export const CostBox = styled.div`
  margin-top: ${spacing.xs};
  z-index: 0;

  span {
    font-size: ${fontSize.p3};
    color: ${palette.black};
  }
`;

export const ExplainBlock = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2.5rem;
  padding: 0 ${spacing.xl};

  ${breakpoints.down('xl')} {
    padding: 0;
  }

  ${breakpoints.down('md')} {
    display: block;
    padding-bottom: 1rem;
  }
`;

export const ExplainSection = styled.div`
  width: calc(100% - 360px);
  padding-right: 10rem;

  p.title {
    font-weight: 600;
  }

  p.desc {
    font-size: ${fontSize.p3};
    margin-top: ${spacing.xs};
  }

  p.desc + p.title {
    margin-top: ${spacing.xxl};
  }

  ${breakpoints.down('xxl')} {
    padding-right: 4rem;
  }

  ${breakpoints.down('xl')} {
    padding-right: 6rem;
  }

  ${breakpoints.down('lg')} {
    width: calc(100% - 312px);
    padding-right: ${spacing.xl};
  }

  ${breakpoints.down('md')} {
    width: 100%;
    padding-right: 0;

    p.desc + p.title {
      margin-top: ${spacing.xl};
    }
  }
`;

export const GuideSection = styled.div`
  width: 22.5rem;

  ${breakpoints.down('lg')} {
    width: 19.5rem;
  }

  ${breakpoints.down('md')} {
    width: 100%;
    margin-top: 2.5rem;
    margin-bottom: 4rem;
  }
`;

export const GuideBox = styled.div<{ isExplore?: boolean }>`
  padding: ${spacing.m};
  box-shadow: 0px 4px 8px rgba(21, 22, 25, 0.05);
  backdrop-filter: blur(4px);
  border-radius: 16px;
  cursor: pointer;
  background: linear-gradient(90deg, #fbfffb 10.87%, #eafbfc 97.01%);
  transition: background-color 0.2s;

  a {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  :hover {
    background: linear-gradient(
        0deg,
        rgba(9, 81, 149, 0.05),
        rgba(9, 81, 149, 0.05)
      ),
      linear-gradient(90deg, #ffffff 10.87%, #fbfffb 74.13%, #eafbfc 97.01%);
  }

  :active {
    background: linear-gradient(
        0deg,
        rgba(9, 81, 149, 0.1),
        rgba(9, 81, 149, 0.1)
      ),
      linear-gradient(90deg, #ffffff 10.87%, #fbfffb 74.13%, #eafbfc 97.01%);
  }

  :nth-of-type(n + 2) {
    margin-top: ${spacing.l};
  }

  ${({ isExplore }) =>
    isExplore &&
    css`
      background: #ffffff;

      a {
        display: block;
      }

      :hover {
        background: ${palette.gray100};
      }

      :active {
        background: ${palette.gray200};
      }
    `}
`;

export const GuideTitleBlock = styled.div<{ isExplore?: boolean }>`
  width: 100%;
  max-width: ${({ isExplore }) => (isExplore ? 'none' : '210px')};
`;

export const GuideTitleBox = styled.div`
  span {
    font-weight: 600;
    color: ${palette.blue700};
  }
`;

export const GuideDescBox = styled.div`
  margin-top: ${spacing.s};
  line-height: 16px;

  span {
    font-size: ${fontSize.p5};
    color: ${palette.black};
  }
`;

export const GuideExploreButtonBox = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${spacing.s};
`;

export const GuideExploreButton = styled.button`
  padding: ${spacing.xs} ${spacing.m};
  background-color: ${palette.blue500};
  color: #ffffff;
  font-size: ${fontSize.p3};
  font-weight: 600;
  border-radius: 24px;
  transition: background-color 0.2s;

  :hover {
    background-color: ${() => Color(palette.blue500).darker(5)};
  }
`;

export const ErrorBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  ${breakpoints.down('md')} {
    height: ${({ theme }) => theme.rem(400)};
  }
`;

export const ErrorTitleBlock = styled.div`
  margin-top: ${spacing.xl};

  span {
    font-size: ${fontSize.h4};
    font-weight: 800;
    color: ${palette.black};
  }

  ${breakpoints.down('md')} {
    margin-top: ${spacing.l};
    span {
      font-size: ${fontSize.p3};
    }
  }
`;

export const ErrorDescBlock = styled.div`
  text-align: center;
  margin-top: ${spacing.s};

  p {
    color: ${palette.gray900};
  }

  ${breakpoints.down('md')} {
    margin-top: ${spacing.xs};

    p {
      font-size: ${fontSize.p5};
    }
  }
`;

export const RefreshButton = styled(Button)`
  display: flex;
  align-items: center;
  margin-top: ${spacing.m};

  span {
    margin-left: ${spacing.xs};
    font-weight: 600;
  }

  ${breakpoints.down('md')} {
    span {
      font-size: ${fontSize.p4};
    }
  }
`;

export const ClearButton = styled.button`
  position: absolute;
  top: -8px;
  right: 16px;
  z-index: 1;
  border: none;
  font-weight: 600;
  font-size: ${fontSize.p4};
  color: ${palette.purple600};
  padding: ${spacing.s} ${spacing.m};
  border-radius: 12px;
  transition: background-color 0.2s;

  :hover {
    background-color: ${Color(palette.purple600).fade(0.9)};
  }
`;
